import './SearchInputStyles.scss';

import { DateRange, DateRangePicker, Divider, Radio, RadioGroup, Typography } from '@trackman/web-shared-components';
import { useEffect, useMemo, useState } from 'react';

import AutocompleteWithChips from 'components/Basic/AutocompleteWithChips/AutocompleteWithChips';
import { DateRangeDto } from 'types/CodegenReportTypes';
import { useGetLookupSeasons } from 'hooks/CodegenLookupHooks';
import { useTranslation } from 'react-i18next';
import { useAutoAnimate } from '@formkit/auto-animate/react';

type Props = {
  selectedSeasonNames: string[] | undefined;
  onChange: (selectedSeasonNames: string[] | undefined, dateRanges: DateRangeDto[] | undefined) => void;
};

type TimePeriod = 'seasons' | 'timeRange';

const SelectTimePeriod = ({ selectedSeasonNames, onChange }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetLookupSeasons();

  const [timePeriod, setTimePeriod] = useState<TimePeriod>('seasons');
  const [tempDateRange, setTempDateRange] = useState<DateRange<Date>>([null, null]);

  const allSeasonIds = useMemo(() => data?.map((datum) => datum.id) ?? [], [data]);
  const allSeasonIdsToNames = useMemo(() => new Map(data?.map((datum) => [datum.id, datum.name])), [data]);
  const allSeasonNamesToIds = useMemo(() => new Map(data?.map((datum) => [datum.name, datum.id])), [data]);
  const minDate = useMemo(() => data?.map((x) => new Date(x.startDate)).sort((a, b) => a.getTime() - b.getTime())[0], [data]);

  const [parent] = useAutoAnimate();

  useEffect(() => {
    setTempDateRange([null, null]);
    onChange(undefined, undefined);
  }, [timePeriod]);

  return (
    <>
      <Typography variant='label bold' component='div' color='#A0A0A0'>
        {t('Time period')}
      </Typography>
      <RadioGroup
        row
        style={{ gap: '24px' }}
        value={timePeriod}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTimePeriod((event.target as HTMLInputElement).value as TimePeriod)}
      >
        <Radio value={'seasons' satisfies TimePeriod} label={t('Season(s)')} />
        <Radio value={'timeRange' satisfies TimePeriod} label={t('Time range')} />
      </RadioGroup>
      <div className='SelectTimePeriod-container' ref={parent}>
        {timePeriod === 'seasons' && (
          <AutocompleteWithChips
            label={t('Seasons')}
            options={allSeasonIds}
            selected={selectedSeasonNames?.map((name) => allSeasonNamesToIds.get(name) ?? t('Nonexistent season')) ?? []}
            setSelected={(selectedIds) => {
              const seasonNames = selectedIds.map((id) => allSeasonIdsToNames.get(id) ?? t('Nonexistent season'));
              const dateRanges = selectedIds
                .map((id) => {
                  const season = data?.find((datum) => datum.id === id);
                  return season ? { utcDateFrom: season.startDate, utcDateTo: season.endDate } : undefined;
                })
                .filter(Boolean) as DateRangeDto[];
              onChange(seasonNames, dateRanges);
            }}
            getOptionLabel={(option) =>
              isLoading ? t('Loading seasons...') : (allSeasonIdsToNames.get(option) ?? t('Nonexistent season'))
            }
            noOptionsText={t(isLoading ? t('Loading seasons...') : t('No seasons found'))}
          />
        )}
        {timePeriod === 'timeRange' && (
          <>
            <DateRangePicker
              className='SelectTimePeriod-picker'
              minDate={minDate}
              disableFuture
              startInputProps={{ label: t('Start date') }}
              endInputProps={{ label: t('End date') }}
              value={tempDateRange}
              onChange={(newDateRange) => {
                setTempDateRange(newDateRange);
                if (newDateRange[0] && newDateRange[1])
                  onChange(undefined, [
                    {
                      utcDateFrom: newDateRange[0].toISOString(),
                      utcDateTo: newDateRange[1].toISOString(),
                    },
                  ]);
              }}
            />
            <Divider />
          </>
        )}
      </div>
    </>
  );
};

export default SelectTimePeriod;
